import { SelectionHeader } from '@/components/bulkQueue/cells/SelectionHeader';
import { useBulkServicesContext } from '@/components/bulkQueue/tables/services/BulkServicesContext';
import {
    CellProps,
    HeaderProps,
    ServicesTableRow,
    ServicesVisitRequestRow,
    ServicesVisitRow,
    VisitCellProps,
    VisitRequestCellProps,
    adaptVisitToServicesVisitRequestRow,
} from '@/components/bulkQueue/types';
import { AxleIconButton } from '@/components/buttons/AxleIconButton';
import { AxleAutocompleteMultiselectWithShortChips } from '@/components/formFields/multiselectFields';
import { NoneLabel } from '@/components/labels/NoneLabel';
import { HorizontalStack, VerticalStack } from '@/components/utils/linearLayouts';
import { ServiceDisciplinesLabel } from '@/components/visitRequests/tables/cells';
import { useInputState } from '@/utils/inputState';
import { Clear, PlaylistAdd } from '@mui/icons-material';
import { Box, Checkbox, Typography } from '@mui/material';
import { FC, useCallback, useEffect } from 'react';

import {
    IClinicianAppointmentSelectedServices,
    IFocusableVisitRequest,
    ISelectedAppointmentServices,
    IVisitRequestSelectionState,
} from './useBulkAppointmentServicesSelection';

type EditingAppointmentProps = {
    appointment: IFocusableVisitRequest['appointments'][number];
    availableServices: IFocusableVisitRequest['availableServices'];
    selectedServiceIds: string[];
    setSelectedServiceIds: (index: number, services: string[]) => void;
    isEnabled: boolean;
    index: number;
    isFocused: boolean;
};

const EditingAppointment: FC<EditingAppointmentProps> = (props) => {
    const {
        appointment,
        selectedServiceIds,
        setSelectedServiceIds,
        index,
        isEnabled,
        isFocused,
        availableServices,
    } = props;

    const setSelectedServices = useCallback(
        (serviceIds: string[]) => {
            setSelectedServiceIds(index, serviceIds);
        },
        [index, setSelectedServiceIds],
    );

    return (
        <AppointmentServiceAssignmentInput
            key={appointment.id}
            enabled={isEnabled}
            isFocused={isFocused}
            selectedServices={selectedServiceIds}
            setSelectedServices={setSelectedServices}
            options={availableServices}
        />
    );
};

type EditingServicesCellProps = {
    appointments: IFocusableVisitRequest['appointments'];
    availableServices: IFocusableVisitRequest['availableServices'];
    selectedAppointmentServices: IClinicianAppointmentSelectedServices[];
    onCancelEditing: () => void;
    setSelectedAppointmentServices: (services: IClinicianAppointmentSelectedServices[]) => void;
    isEnabled: boolean;
    isFocused: boolean;
};

const EMPTY_ARRAY: string[] = [];

const EditingServicesCell: FC<EditingServicesCellProps> = (props) => {
    const {
        appointments,
        availableServices,
        isEnabled,
        isFocused,
        onCancelEditing,
        selectedAppointmentServices,
        setSelectedAppointmentServices,
    } = props;

    const setSelectedServiceIds = useCallback(
        (index: number, serviceIds: string[]) => {
            setSelectedAppointmentServices(
                selectedAppointmentServices.map((appointment, i) => {
                    if (i === index) {
                        return { ...appointment, serviceIds };
                    }
                    return appointment;
                }),
            );
        },
        [selectedAppointmentServices, setSelectedAppointmentServices],
    );
    return (
        <HorizontalStack>
            <Box flexGrow={1}>
                {appointments.map((appointment, index) => (
                    <EditingAppointment
                        key={appointment.id}
                        appointment={appointment}
                        availableServices={availableServices}
                        index={index}
                        isEnabled={isEnabled}
                        isFocused={isFocused}
                        selectedServiceIds={
                            selectedAppointmentServices.at(index)?.serviceIds ?? EMPTY_ARRAY
                        }
                        setSelectedServiceIds={setSelectedServiceIds}
                    />
                ))}
            </Box>
            <AxleIconButton icon={<Clear />} onClick={onCancelEditing} tooltip='Cancel' />
        </HorizontalStack>
    );
};

type ServicesSelectionCellProps = {
    selectionInfo: IVisitRequestSelectionState;
    selectedAppointmentServices: ISelectedAppointmentServices;
    hasFocusedVisitRequest: boolean;
    enabled: boolean;
};

const AppointmentServiceAssignmentInput = ({
    enabled,
    selectedServices,
    setSelectedServices,
    options,
}: {
    options: { id: string; name: string }[];
    isFocused: boolean;
    selectedServices: string[];
    setSelectedServices: (services: string[]) => void;
    enabled: boolean;
}) => {
    const inputState = useInputState<string[]>(selectedServices, { required: false });
    useEffect(() => {
        if (
            inputState.value.length !== selectedServices.length ||
            inputState.value.some((v, i) => v !== selectedServices[i])
        ) {
            setSelectedServices(inputState.value);
        }
    }, [inputState.value, selectedServices, setSelectedServices]);
    return (
        <AxleAutocompleteMultiselectWithShortChips
            inputState={inputState}
            options={options}
            optionToLabel={(service) => service.name}
            optionToValue={(service) => service.id}
            enabled={enabled}
        />
    );
};

type ServicesAssignmentEditorCellProps = {
    disciplines: { id: string; name: string | null; displayName: string; code: string }[];
    availableServices: { id: string; name: string }[];
    appointments: { id: string; services: { id: string; name: string }[] }[];
    selectionInfo: IVisitRequestSelectionState;
    selectedAppointmentServices: ISelectedAppointmentServices;
    hasFocusedVisitRequest: boolean;
    enabled: boolean;
};

export const ServicesAssignmentEditorCell: FC<ServicesAssignmentEditorCellProps> = (props) => {
    const {
        disciplines,
        availableServices,
        appointments,
        selectionInfo,
        selectedAppointmentServices: {
            value: selectedAppointmentServices,
            setValue: setSelectedAppointmentServices,
            toDisplay: selectedAppointmentsToDisplay,
        },
        hasFocusedVisitRequest,
        enabled,
    } = props;
    const { isFocused, isSelected, isCompatibleWithFocusedEditing } = selectionInfo;

    return (
        <HorizontalStack
            justifyContent='space-between'
            onClick={(e) => {
                // Prevent clicks in this cell from propagating to the row.
                e.stopPropagation();
            }}
        >
            <VerticalStack width='100%'>
                <ServiceDisciplinesLabel disciplines={disciplines} />
                {isFocused ? (
                    <EditingServicesCell
                        selectedAppointmentServices={selectedAppointmentServices}
                        setSelectedAppointmentServices={setSelectedAppointmentServices}
                        onCancelEditing={() => {
                            selectionInfo.cancelFocus();
                        }}
                        appointments={appointments}
                        availableServices={availableServices}
                        isEnabled={enabled}
                        isFocused={isFocused}
                    />
                ) : isSelected ? (
                    selectedAppointmentsToDisplay.map((appointment, index) => (
                        <VerticalStack key={index}>
                            {appointment.services.length > 0 ? (
                                appointment.services.map((service) => (
                                    <Typography key={service.id}>{service.name}</Typography>
                                ))
                            ) : (
                                <NoneLabel text='No services' />
                            )}
                        </VerticalStack>
                    ))
                ) : (
                    appointments.map((appointment) => (
                        <VerticalStack key={appointment.id}>
                            {appointment.services.length > 0 ? (
                                appointment.services.map((service) => (
                                    <Typography key={service.id}>{service.name}</Typography>
                                ))
                            ) : (
                                <NoneLabel text='No services' />
                            )}
                        </VerticalStack>
                    ))
                )}
            </VerticalStack>
            {hasFocusedVisitRequest ? (
                // Editing mode is on and this isn't the row being edited directly,
                // but this row is compatible with the current editing state.
                !isFocused && isCompatibleWithFocusedEditing ? (
                    <Checkbox
                        size='small'
                        checked={isSelected}
                        onChange={(e) => {
                            selectionInfo.setIsSelected(e.target.checked);
                        }}
                    />
                ) : null
            ) : (
                // Editing Mode is off, any row could be used to start editing.
                <AxleIconButton
                    icon={<PlaylistAdd />}
                    onClick={() => {
                        selectionInfo.focus();
                    }}
                    tooltip='Add services'
                />
            )}
        </HorizontalStack>
    );
};

export const ServicesVisitRequestSelectionCell: FC<
    VisitRequestCellProps<ServicesSelectionCellProps, ServicesVisitRequestRow>
> = (props) => {
    const { entity, selectedAppointmentServices, hasFocusedVisitRequest, selectionInfo, enabled } =
        props;
    return (
        <ServicesAssignmentEditorCell
            disciplines={entity.disciplines}
            availableServices={entity.availableServices}
            appointments={entity.appointments}
            selectionInfo={selectionInfo}
            selectedAppointmentServices={selectedAppointmentServices}
            hasFocusedVisitRequest={hasFocusedVisitRequest}
            enabled={enabled}
        />
    );
};

type LegacyServicesVisitRequestSelectionCellProps = ServicesSelectionCellProps & {
    visitRequest: {
        id: string;
        availableServices: { id: string; name: string }[];
        disciplines: { id: string; displayName: string }[];
        appointments: {
            id: string;
            services: { id: string; name: string }[];
            clinician: {
                id: string;
                firstName: string;
                lastName: string;
                logoUrl: string | null;
            } | null;
        }[];
    };
};

export const LegacyServicesVisitRequestSelectionCell: FC<
    LegacyServicesVisitRequestSelectionCellProps
> = ({ visitRequest, ...rest }) => {
    return (
        <ServicesVisitRequestSelectionCell
            entity={visitRequest as ServicesVisitRequestRow}
            {...rest}
        />
    );
};

export const ServicesVisitSelectionCell: FC<
    VisitCellProps<ServicesSelectionCellProps, ServicesVisitRow>
> = (props) => {
    const { entity, ...rest } = props;
    const visitRequest = entity.visitRequests.at(0);
    if (!visitRequest) {
        return <NoneLabel />;
    }
    return (
        <ServicesVisitRequestSelectionCell
            entity={adaptVisitToServicesVisitRequestRow(entity, visitRequest)}
            {...rest}
        />
    );
};

// Visit-native cell for the unified workflow table — sources `availableServices` from the caller
// (org services scoped to the visit's disciplines) and `appointments` from the visit's clinician
// appointments directly. No visit-request adapter, in keeping with the sunset-visit-requests milestone.
type ServicesUnifiedVisitSelectionCellProps = ServicesSelectionCellProps & {
    visit: {
        id: string;
        disciplines: { id: string; name: string | null; displayName: string; code: string }[];
        appointments: { id: string }[];
        clinicianAppointments: {
            id: string;
            services: { id: string; name: string }[];
        }[];
    };
    availableServices: { id: string; name: string }[];
};

export const ServicesUnifiedVisitSelectionCell: FC<ServicesUnifiedVisitSelectionCellProps> = ({
    visit,
    availableServices,
    selectionInfo,
    selectedAppointmentServices,
    hasFocusedVisitRequest,
    enabled,
}) => {
    const appointments = visit.clinicianAppointments.map((appointment) => ({
        id: appointment.id,
        services: appointment.services.map((service) => ({ id: service.id, name: service.name })),
    }));
    return (
        <ServicesAssignmentEditorCell
            disciplines={visit.disciplines}
            availableServices={availableServices}
            appointments={appointments}
            selectionInfo={selectionInfo}
            selectedAppointmentServices={selectedAppointmentServices}
            hasFocusedVisitRequest={hasFocusedVisitRequest}
            enabled={enabled}
        />
    );
};

export const ServicesSelectionCell: FC<CellProps<ServicesSelectionCellProps, ServicesTableRow>> = (
    props,
) => {
    const { entity, ...rest } = props;

    switch (entity.__typename) {
        case 'VisitRequest':
            return <ServicesVisitRequestSelectionCell entity={entity} {...rest} />;
        case 'Visit':
            return <ServicesVisitSelectionCell entity={entity} {...rest} />;
        default:
            return null;
    }
};

// Context-based version (used by BulkServicesAssignmentTable)
export const ContextServicesSelectionCell: FC<{ entity: ServicesTableRow }> = (props) => {
    const { entity } = props;
    const selection = useBulkServicesContext();

    switch (entity.__typename) {
        case 'VisitRequest':
            return (
                <ServicesVisitRequestSelectionCell
                    entity={entity}
                    hasFocusedVisitRequest={selection.hasFocusedVisitRequest}
                    selectionInfo={selection.getSelectionState(entity)}
                    selectedAppointmentServices={selection.selectedAppointmentServices}
                    enabled={selection.state !== 'submitting'}
                />
            );
        case 'Visit': {
            const visitRequest = entity.visitRequests.at(0);
            if (!visitRequest) {
                return <NoneLabel />;
            }
            return (
                <ServicesVisitRequestSelectionCell
                    entity={adaptVisitToServicesVisitRequestRow(entity, visitRequest)}
                    hasFocusedVisitRequest={selection.hasFocusedVisitRequest}
                    selectionInfo={selection.getSelectionState(entity)}
                    selectedAppointmentServices={selection.selectedAppointmentServices}
                    enabled={selection.state !== 'submitting'}
                />
            );
        }
        default:
            return null;
    }
};

export const ServicesSelectionHeader: FC<HeaderProps> = (props) => {
    const selection = useBulkServicesContext();
    return (
        <SelectionHeader
            {...props}
            hasItems={selection.entityCount > 0}
            isDisabled={
                selection.state === 'submitting' ||
                selection.selectedCount === 0 ||
                selection.numSelectedAppointmentServices === 0
            }
            numSelected={selection.selectedCount}
            summaryString={selection.summaryString}
            onApply={selection.submit}
        />
    );
};
